import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, User } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero, EmptyState, LazyImg, ShareButtons } from "../../components/Bits";

export function Articles() {
  const { t, lang } = useApp();
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get(`/public/articles?page=${page}&limit=9`).then((r) => setData(r.data)).catch(() => {});
  }, [page]);

  return (
    <div data-testid="articles-page">
      <SEO title={t("articles")} />
      <PageHero title={t("articles")} sub={lang === "hi" ? "फाउंडेशन के लेख और विचार।" : "Articles, stories and perspectives from the Foundation."} />
      <section className="py-16">
        <div className="container-ssf">
          {data.items.length === 0 && <EmptyState text="Articles will appear here once published." />}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.items.map((a) => (
              <Link key={a.id} to={`/articles/${a.slug}`} data-testid={`article-card-${a.slug}`}
                className="card-ssf group flex flex-col overflow-hidden transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-xl">
                {a.image && <LazyImg src={a.image} alt={a.alt_text || a.title} className="aspect-[16/9] w-full object-cover" />}
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-saffron-600">{a.category}</span>
                  <h2 className="mt-2 font-display text-xl font-bold text-navy group-hover:text-saffron-600">{lang === "hi" && a.title_hi ? a.title_hi : a.title}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                    {a.author && <span className="inline-flex items-center gap-1"><User size={13} />{a.author}</span>}
                    {a.date && <span className="inline-flex items-center gap-1"><CalendarDays size={13} />{a.date}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {data.total > 9 && (
            <div className="mt-10 flex justify-center gap-3">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="btn-ghost !px-5 !py-2 text-sm disabled:opacity-40" data-testid="articles-prev">←</button>
              <span className="py-2 text-sm font-semibold text-navy">{page}</span>
              <button disabled={page * 9 >= data.total} onClick={() => setPage(page + 1)} className="btn-ghost !px-5 !py-2 text-sm disabled:opacity-40" data-testid="articles-next">→</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const { t, lang } = useApp();
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/public/articles/${slug}`).then((r) => setA(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <PageHero title={t("not_found")} />;
  if (!a) return null;
  const title = lang === "hi" && a.title_hi ? a.title_hi : a.title;
  const body = lang === "hi" && a.body_hi ? a.body_hi : a.body;

  return (
    <div data-testid="article-detail">
      <SEO title={a.seo_title || title} description={a.seo_desc || a.excerpt} image={a.image} keywords={a.seo_keywords} />
      <PageHero title={title} sub={a.subtitle || a.excerpt} />
      <article className="py-14">
        <div className="container-ssf max-w-3xl">
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
            {a.author && <span className="inline-flex items-center gap-1.5"><User size={15} />{a.author}</span>}
            {a.date && <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} />{a.date}</span>}
            {a.category && <span className="rounded-full bg-saffron-100 px-3 py-1 text-xs font-bold text-saffron-600">{a.category}</span>}
          </div>
          {a.image && <LazyImg src={a.image} alt={a.alt_text || title} className="mt-8 w-full rounded-2xl object-cover" />}
          {/<[a-z][\s\S]*>/i.test(body || "")
            ? <div className="prose-ssf mt-8 text-base text-slate-700" data-testid="article-body" dangerouslySetInnerHTML={{ __html: body }} />
            : <div className="mt-8 whitespace-pre-wrap text-base leading-relaxed text-slate-700" data-testid="article-body">{body}</div>}
          {Array.isArray(a.tags) && a.tags.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {a.tags.map((tag) => <span key={tag} className="rounded-full bg-navy-50 px-3 py-1 text-xs font-semibold text-navy">#{tag}</span>)}
            </div>
          )}
          <div className="mt-10 border-t border-gray-200 pt-8"><ShareButtons title={title} text={a.excerpt} /></div>
        </div>
      </article>
    </div>
  );
}
